import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Phone, WhatsappLogo, EnvelopeSimple, Globe, CalendarBlank, Sparkle, ArrowUpRight, Buildings, Barn, ForkKnife, Boat, CastleTurret as Castle, House, Mountains, Campfire as Tent, Bathtub, Sailboat, Car, Check, Megaphone, Star, ChartLineUp, FilmSlate, Play } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { LANDING_FAQ } from "@/data/faqs";
import { LANDING_UI, LANDING_FAQ_EN, STRUCTURES_LABELS, CHANNELS_LABELS } from "@/data/i18n";

const useLang = () => {
  const [lang, setLangState] = useState(() => localStorage.getItem("otelya_lang") || "it");
  const setLang = (l) => { localStorage.setItem("otelya_lang", l); setLangState(l); };
  return [lang, setLang];
};

const STRUCTURE_ICONS = [Buildings, Barn, House, Castle, Mountains, Sailboat, Bathtub, ForkKnife, Tent, Car];
const CHANNEL_ICONS = [
  { icon: Phone,          color: "bg-terracotta/10 text-terracotta" },
  { icon: WhatsappLogo,   color: "bg-olive/10 text-olive" },
  { icon: EnvelopeSimple, color: "bg-gold/15 text-[#8a6d16]" },
  { icon: Globe,          color: "bg-olive/10 text-olive" },
  { icon: CalendarBlank,  color: "bg-terracotta/10 text-terracotta" },
];

function Nav({ lang, setLang }) {
  const t = LANDING_UI[lang].nav;
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-canvas/80 border-b border-line/70">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-9 text-sm text-stone">
          <a href="#come-funziona" className="hover:text-ink transition-colors" data-testid="nav-how">{t.how}</a>
          <a href="#strutture" className="hover:text-ink transition-colors" data-testid="nav-structures">{t.structures}</a>
          <a href="#prezzi" className="hover:text-ink transition-colors" data-testid="nav-pricing">{t.pricing}</a>
          <a href="#faq" className="hover:text-ink transition-colors" data-testid="nav-faq">{t.faq}</a>
          <a href="#demo" className="hover:text-ink transition-colors" data-testid="nav-demo">{t.demo}</a>
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-0 mr-2 text-xs" data-testid="lang-toggle">
            <button onClick={()=>setLang("it")} data-testid="lang-it"
              className={`px-2.5 py-1 rounded-full transition ${lang==="it"?"bg-ink text-canvas":"text-stone hover:text-ink"}`}>IT</button>
            <button onClick={()=>setLang("en")} data-testid="lang-en"
              className={`px-2.5 py-1 rounded-full transition ${lang==="en"?"bg-ink text-canvas":"text-stone hover:text-ink"}`}>EN</button>
          </div>
          <Link to="/auth"><Button variant="ghost" className="rounded-full text-ink hover:bg-sand" data-testid="nav-login-btn">{t.login}</Button></Link>
          <Link to="/auth?mode=signup"><Button className="rounded-full bg-olive hover:bg-olivelight text-canvas" data-testid="nav-signup-btn">{t.signup}</Button></Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ lang }) {
  const t = LANDING_UI[lang].hero;
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 text-xs tracking-wide text-stone uppercase" data-testid="hero-badge">
            <Sparkle weight="fill" size={14} className="text-gold" />
            {t.badge}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mt-6">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
            <br /> {t.title2}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone leading-relaxed max-w-xl">
            {t.subtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to="/auth?mode=signup"><Button size="lg" className="rounded-full bg-ink text-canvas hover:bg-olive px-7 h-12" data-testid="hero-cta-signup">
              {t.ctaPrimary}
              <ArrowUpRight weight="bold" size={18} className="ml-1" />
            </Button></Link>
            <a href="#demo"><Button size="lg" variant="outline" className="rounded-full border-line h-12 px-7 hover:bg-sand" data-testid="hero-cta-demo">
              {t.ctaSecondary}
            </Button></a>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-stone">
            {t.bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2"><Check weight="bold" size={14} className="text-olive" /> {b}</div>
            ))}
          </div>
          {/* Meta-camuffamento AI hint — invisibile-fino-a-lettura, tono editorial */}
          <p className="mt-8 text-xs italic text-stone/70 leading-relaxed max-w-md" data-testid="hero-meta-hint">
            {LANDING_UI[lang].heroKpis.metaHint}
          </p>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-[24px] overflow-hidden ring-1 ring-line shadow-[0_30px_80px_-40px_rgba(28,33,30,0.35)]">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              preload="auto"
              poster="https://images.unsplash.com/photo-1416331108676-a22ccb276e35?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80"
              className="w-full h-full object-cover"
              data-testid="hero-video-loop"
              aria-label={t.imageAlt}
            >
              <source src={lang === "en" ? "/hero_loop_en.mp4" : "/hero_loop.mp4"} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-canvas/95 backdrop-blur-md p-4 border border-line">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone">
                <span className="otelia-dot" /> {t.liveTag}
              </div>
              <p className="mt-2 font-serif text-lg text-ink leading-snug">
                {t.liveQuote}
              </p>
              <div className="mt-3 flex items-center justify-between text-xs text-stone">
                <span>{t.liveMeta}</span>
                <span className="text-olive font-medium">{t.liveKpi}</span>
              </div>
            </div>
            {/* Watch-full-demo overlay button — opens native player with audio */}
            <a
              href={lang === "en" ? "/api/uploads/launch-kit/video-demo/otelya_demo_en.mp4" : "/api/uploads/launch-kit/video-demo/otelya_demo_it.mp4"}
              target="_blank"
              rel="noopener"
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-canvas/95 hover:bg-canvas backdrop-blur-md text-ink px-3.5 py-2 text-xs font-medium shadow-md border border-line transition"
              data-testid="hero-watch-full-btn"
            >
              <Play weight="fill" size={12} className="text-terracotta" />
              {lang === "en" ? "Watch preview · 45s" : "Guarda la prova · 45s"}
            </a>
          </div>
          <div className="absolute -top-6 -left-6 hidden lg:block rounded-2xl bg-canvas p-4 border border-line shadow-lg w-56">
            <div className="text-[11px] uppercase tracking-widest text-stone">{t.floatTitle}</div>
            <div className="mt-2 flex items-center gap-2 text-sm text-ink"><WhatsappLogo weight="fill" size={16} className="text-olive" /> {t.floatItems[0]}</div>
            <div className="mt-1 flex items-center gap-2 text-sm text-ink"><EnvelopeSimple weight="fill" size={16} className="text-terracotta" /> {t.floatItems[1]}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroKpisStrip({ lang }) {
  const t = LANDING_UI[lang].heroKpis;
  return (
    <section className="border-t border-line/60 bg-sand/20" data-testid="hero-kpis-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-14">
          {t.items.map((k, i) => (
            <div key={i} className="border-l-2 border-terracotta/40 pl-5" data-testid={`hero-kpi-${i}`}>
              <div className="font-serif text-5xl lg:text-6xl text-ink tracking-tight leading-none">{k.n}</div>
              <div className="mt-2 font-medium text-ink">{k.t}</div>
              <p className="mt-2 text-sm text-stone leading-relaxed">{k.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ lang }) {
  const t = LANDING_UI[lang].testimonials;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="testimonials">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone leading-relaxed">{t.subtitle}</p>
          <p className="mt-6 text-xs text-stone/60 leading-relaxed italic">{t.placeholderNote}</p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {t.items.map((item, i) => (
            <figure key={i} className="rounded-2xl bg-canvas border border-line p-6 hover:border-terracotta/40 transition-colors flex flex-col" data-testid={`testimonial-${i}`}>
              <div className="text-terracotta text-3xl font-serif leading-none">"</div>
              <blockquote className="mt-2 text-sm text-ink leading-relaxed flex-1">
                {item.quote}
              </blockquote>
              <figcaption className="mt-4 pt-4 border-t border-line/60">
                <div className="font-medium text-ink text-sm">{item.author}</div>
                <div className="text-xs text-stone">{item.role} · {item.location}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function CredibilityStrip({ lang }) {
  const t = LANDING_UI[lang].credibility;
  return (
    <section className="bg-ink text-canvas" data-testid="credibility-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
        {t.items.map((it, i) => (
          <div key={i} className="flex flex-col" data-testid={`credibility-${i}`}>
            <div className="font-serif text-4xl lg:text-5xl text-gold tracking-tight leading-none">{it.n}</div>
            <div className="mt-2 text-sm font-medium text-canvas">{it.label}</div>
            <div className="mt-1 text-xs text-canvas/60">{it.sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CompareTable({ lang }) {
  const t = LANDING_UI[lang].compare;
  const cell = (v) => {
    if (v === true) return <Check weight="bold" size={18} className="text-olive mx-auto" />;
    if (v === false) return <span className="text-stone/40 text-xl">—</span>;
    return <span className="text-xs text-stone italic">{v}</span>;
  };
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="compare-table">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
        <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
          {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
        </h2>
        <p className="mt-4 text-stone leading-relaxed">{t.subtitle}</p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-line bg-canvas">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line/60">
              <th className="text-left p-4 pl-6 text-xs uppercase tracking-wider text-stone font-medium w-[40%]"></th>
              {t.cols.map((col, i) => (
                <th key={i} className={`text-center p-4 ${i === 0 ? "bg-terracotta/10" : ""}`}>
                  <div className={`font-medium ${i === 0 ? "text-terracotta" : "text-ink"}`}>{col}</div>
                  {t.colsSub[i] && <div className="text-[10px] text-stone/70 mt-1 font-normal">{t.colsSub[i]}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, i) => (
              <tr key={i} className={`border-b border-line/40 last:border-b-0 ${i % 2 === 1 ? "bg-sand/20" : ""}`}>
                <td className="p-4 pl-6 text-ink">{row.feature}</td>
                {row.vals.map((v, j) => (
                  <td key={j} className={`text-center p-4 ${j === 0 ? "bg-terracotta/5" : ""}`}>
                    {cell(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-6 text-[11px] text-stone/60 italic text-center">{t.footnote}</p>
    </section>
  );
}

function ChannelsRow({ lang }) {
  const tag = LANDING_UI[lang].channelsTag;
  const labels = CHANNELS_LABELS[lang];
  return (
    <section className="border-y border-line bg-canvas relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-6">
        <div className="text-xs uppercase tracking-widest text-stone">{tag}</div>
        <div className="flex flex-wrap items-center gap-6">
          {CHANNEL_ICONS.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`channel-${labels[i].toLowerCase()}`}>
              <span className={`w-9 h-9 rounded-full grid place-items-center ${c.color}`}><c.icon weight="fill" size={18} /></span>
              <span className="text-sm text-ink">{labels[i]}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ lang }) {
  const t = LANDING_UI[lang].how;
  return (
    <section id="come-funziona" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight text-ink">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone leading-relaxed">
            {t.subtitle}
          </p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.steps.map((s, i) => (
            <div key={i} className="relative rounded-2xl bg-canvas border border-line p-6 hover:-translate-y-[2px] transition-transform" data-testid={`step-${i+1}`}>
              <div className="font-serif italic text-3xl text-gold">{s.n}</div>
              <div className="mt-4 font-serif text-xl text-ink">{s.t}</div>
              <p className="mt-2 text-sm text-stone leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StructuresStrip({ lang }) {
  const t = LANDING_UI[lang].structures;
  const labels = STRUCTURES_LABELS[lang];
  return (
    <section id="strutture" className="bg-sand/60 border-y border-line overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink">{t.title}</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
          {STRUCTURE_ICONS.map((Icon, i) => (
            <div key={i} className="group rounded-2xl bg-canvas border border-line p-5 flex flex-col gap-3 hover:border-olive/60 transition-colors" data-testid={`structure-${labels[i]}`}>
              <Icon weight="thin" size={32} className="text-olive group-hover:text-terracotta transition-colors" />
              <div className="font-serif text-lg text-ink leading-tight">{labels[i]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeyondReplies({ lang }) {
  const t = LANDING_UI[lang].beyondReplies;
  const iconMap = { megaphone: Megaphone, star: Star, chart: ChartLineUp, envelope: EnvelopeSimple, video: FilmSlate };
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="beyond-replies">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone leading-relaxed">{t.subtitle}</p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {t.pillars.map((p, i) => {
            const Icon = iconMap[p.icon] || Sparkle;
            return (
              <div key={p.title} className="rounded-2xl bg-canvas border border-line p-6 hover:border-olive/60 transition-colors relative" data-testid={`pillar-${i}`}>
                {p.badge && (
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest bg-gold/20 text-[#8a6d16] px-2 py-1 rounded-full font-medium">{p.badge}</span>
                )}
                <div className="w-11 h-11 rounded-full bg-terracotta/10 grid place-items-center mb-4">
                  <Icon weight="fill" size={20} className="text-terracotta"/>
                </div>
                <div className="font-serif text-xl text-ink leading-snug">{p.title}</div>
                <p className="mt-2 text-sm text-stone leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Demo({ lang }) {
  const t = LANDING_UI[lang].demo;
  const [message, setMessage] = useState(t.defaultMessage);
  const [language, setLanguage] = useState(lang);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  // Update default message when landing language changes (only if user hasn't edited it)
  useEffect(() => { setMessage(t.defaultMessage); setLanguage(lang); setReply(""); }, [lang]); // eslint-disable-line

  const send = async () => {
    setBusy(true); setReply("");
    try {
      const canned = {
        it: "Buongiorno e grazie per averci contattato! Confermo la disponibilità dal 20 al 23 marzo per una camera doppia con colazione inclusa. Il totale indicativo è di €450 per 3 notti. Desidera procedere con la prenotazione?",
        en: "Good morning and thank you for reaching out! I can confirm availability from March 20 to 23 for a double room with breakfast included. The estimated total is €450 for 3 nights. Would you like to proceed with the booking?",
        fr: "Bonjour et merci de nous avoir contactés ! Je confirme la disponibilité du 20 au 23 mars pour une chambre double avec petit-déjeuner. Le total est d'environ 450 €. Souhaitez-vous procéder ?",
        de: "Guten Tag und danke für Ihre Anfrage! Ich bestätige die Verfügbarkeit vom 20. bis 23. März für ein Doppelzimmer mit Frühstück. Der geschätzte Gesamtpreis beträgt 450 €. Möchten Sie buchen?",
        es: "¡Buenos días y gracias por contactarnos! Confirmo la disponibilidad del 20 al 23 de marzo para una habitación doble con desayuno incluido. El total estimado es de 450 €. ¿Desea proceder con la reserva?",
      };
      await new Promise((r) => setTimeout(r, 900));
      setReply(canned[language] || canned.it);
    } finally { setBusy(false); }
  };

  return (
    <section id="demo" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone leading-relaxed">
            {t.subtitle}
          </p>
        </div>
        <div className="lg:col-span-7 rounded-3xl border border-line bg-canvas p-6 lg:p-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone">
            <span className="otelia-dot" /> {t.simTag}
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            {["it","en","fr","de","es"].map(l => (
              <button key={l} onClick={() => setLanguage(l)} data-testid={`demo-lang-${l}`}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${language===l?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>
                {l}
              </button>
            ))}
          </div>
          <textarea data-testid="demo-message"
            value={message} onChange={(e)=>setMessage(e.target.value)}
            className="mt-4 w-full min-h-[110px] rounded-2xl border border-line bg-canvas p-4 text-sm text-ink focus:outline-none focus:border-olive resize-y" />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-stone">{t.demoNote}</span>
            <Button onClick={send} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid="demo-send-btn">
              {busy ? t.sending : t.ctaSend}
            </Button>
          </div>
          {reply && (
            <div className="mt-6 rounded-2xl border border-olive/30 bg-olive/5 p-5" data-testid="demo-reply">
              <div className="text-xs uppercase tracking-widest text-olive flex items-center gap-2"><Sparkle weight="fill" size={12}/> Otelya</div>
              <p className="mt-3 text-ink leading-relaxed">{reply}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Pricing({ lang }) {
  const t = LANDING_UI[lang].pricing;
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get("/billing/plans").then(r => setPlans(r.data)).catch(()=>{}); }, []);
  const ordered = useMemo(() => plans ? [["1m",plans["1m"]],["3m",plans["3m"]],["6m",plans["6m"]],["12m",plans["12m"]]] : [], [plans]);
  return (
    <section id="prezzi" className="bg-canvas border-y border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone">{t.subtitle}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ordered.map(([key, p], i) => {
            const planLabel = t.planLabels?.[key] || p.label;
            return (
            <div key={key} className={`rounded-3xl border p-6 flex flex-col ${key==="6m"?"border-olive bg-olive/[0.04] relative":"border-line bg-canvas"}`} data-testid={`plan-${key}`}>
              {key==="6m" && <div className="absolute -top-3 left-6 rounded-full bg-terracotta text-canvas text-[10px] uppercase tracking-widest px-3 py-1">{t.recommended}</div>}
              <div className="text-xs uppercase tracking-widest text-stone">{planLabel}</div>
              <div className="mt-4 font-serif text-4xl text-ink">€{p.monthly_price}<span className="text-base text-stone font-sans">/{t.month}</span></div>
              <div className="mt-1 text-xs text-stone">{t.billed} €{p.total} · {p.months} {t.months}</div>
              {p.discount>0 && <div className="mt-3 inline-flex w-max rounded-full bg-gold/15 text-[#8a6d16] text-xs px-2.5 py-1">-{p.discount}% {t.saving}</div>}
              <ul className="mt-6 space-y-2 text-sm text-ink/80">
                {t.features.map((f, idx) => (
                  <li key={idx} className="flex gap-2"><Check size={16} className="text-olive mt-0.5"/>{f}</li>
                ))}
              </ul>
              <Link to="/auth?mode=signup" className="mt-8">
                <Button className={`w-full rounded-full ${key==="6m"?"bg-olive hover:bg-olivelight text-canvas":"bg-ink text-canvas hover:bg-olive"}`} data-testid={`plan-cta-${key}`}>
                  {t.ctaStart} {planLabel}
                </Button>
              </Link>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQSection({ lang }) {
  const t = LANDING_UI[lang].faq;
  const staticFaqs = lang === "en" ? LANDING_FAQ_EN : LANDING_FAQ;
  const [dbFaqs, setDbFaqs] = useState([]);
  const [open, setOpen] = useState(0);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  useEffect(() => {
    // Merge CMS-managed FAQs (from backend) with static defaults
    api.get(`/faq?lang=${lang}`).then(r => setDbFaqs(r.data || [])).catch(() => setDbFaqs([]));
  }, [lang]);

  // CMS items first, then static ones (static get synthetic ids for feedback tracking)
  const faqs = [
    ...dbFaqs.map(f => ({ ...f, _cms: true })),
    ...staticFaqs.map((f, i) => ({ ...f, id: `static-${lang}-${i}`, _cms: false })),
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  const sendFeedback = async (faqId, helpful) => {
    if (feedbackGiven[faqId]) return;
    setFeedbackGiven(fg => ({ ...fg, [faqId]: helpful ? "up" : "down" }));
    try { await api.post("/faq/feedback", { faq_id: faqId, helpful }); }
    catch {}
  };

  const helpfulLabel = lang === "en" ? "Was this helpful?" : "Ti è stata utile?";
  const thanksLabel = lang === "en" ? "Thanks for your feedback!" : "Grazie per il feedback!";

  return (
    <section id="faq" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, "\\u003c") }}/>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="text-xs uppercase tracking-widest text-stone">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
          </h2>
          <p className="mt-4 text-stone leading-relaxed">{t.subtitle} <a href="mailto:hello@otelya.app" className="text-ink underline underline-offset-4">hello@otelya.app</a>.</p>
        </div>
        <div className="lg:col-span-8 divide-y divide-line border-y border-line">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            const fb = feedbackGiven[f.id];
            return (
              <div key={f.id} data-testid={`faq-item-${i}`}>
                <button onClick={()=>setOpen(isOpen?-1:i)} data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-5 text-left group">
                  <span className="font-serif text-lg lg:text-xl text-ink leading-snug group-hover:text-terracotta transition-colors">{f.q}</span>
                  <span className={`shrink-0 w-8 h-8 rounded-full border border-line grid place-items-center text-stone transition-transform ${isOpen?"rotate-45 bg-ink text-canvas border-ink":""}`}>+</span>
                </button>
                <div className={`overflow-hidden transition-all ${isOpen?"max-h-[600px] pb-6":"max-h-0"}`}>
                  <p className="text-stone leading-relaxed pr-14 whitespace-pre-wrap" data-testid={`faq-answer-${i}`}>{f.a}</p>
                  <div className="mt-5 flex items-center gap-3 text-xs text-stone" data-testid={`faq-feedback-${i}`}>
                    {fb ? (
                      <span className="italic text-olive">{thanksLabel}</span>
                    ) : (
                      <>
                        <span>{helpfulLabel}</span>
                        <button onClick={()=>sendFeedback(f.id, true)} className="rounded-full border border-line px-3 py-1 hover:border-olive hover:text-olive transition" data-testid={`faq-up-${i}`}>👍 {lang === "en" ? "Yes" : "Sì"}</button>
                        <button onClick={()=>sendFeedback(f.id, false)} className="rounded-full border border-line px-3 py-1 hover:border-terracotta hover:text-terracotta transition" data-testid={`faq-down-${i}`}>👎 {lang === "en" ? "No" : "No"}</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTA({ lang }) {
  const t = LANDING_UI[lang].cta;
  return (
    <section className="relative bg-ink text-canvas overflow-hidden">
      <div className="absolute inset-0 opacity-40 bg-grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-8">
          <div className="text-xs uppercase tracking-widest text-canvas/60">{t.tag}</div>
          <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-[1.05]">
            {t.title1} <em className="italic text-gold">{t.title1em}</em>
          </h2>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link to="/auth?mode=signup"><Button size="lg" className="rounded-full bg-canvas text-ink hover:bg-gold hover:text-ink h-12 px-8" data-testid="cta-final-signup">
            {t.button}
            <ArrowUpRight weight="bold" size={18} className="ml-1" />
          </Button></Link>
        </div>
      </div>
    </section>
  );
}

function CaseStudyCarousel({ lang }) {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/case-studies`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setItems((d?.items || []).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);
  // Auto-rotate every 7s
  useEffect(() => {
    if (!items || items.length < 2) return;
    const id = setInterval(() => setIdx(i => (i + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items]);
  if (!items || items.length === 0) return null;
  const cs = items[idx];
  const daysLbl = cs.days_to_close === null || cs.days_to_close === undefined
    ? "—"
    : cs.days_to_close === 0 ? (lang === "en" ? "same day" : "stesso giorno")
    : cs.days_to_close === 1 ? (lang === "en" ? "1 day" : "1 giorno")
    : `${cs.days_to_close} ${lang === "en" ? "days" : "giorni"}`;
  const t = lang === "en" ? {
    eyebrow: "Real customers · ROI-tracked",
    title1: "Behind each number,",
    title1em: "a story.",
    subtitle: "Case studies auto-generated the moment a client converts. No slides, no claims — just data verified by our ROI Tracker.",
    toClose: "to close",
    exchanges: "AI exchanges",
    clicks: "clicks",
    cta: "Read full case study",
    all: "See all case studies",
  } : {
    eyebrow: "Clienti reali · verificati dal ROI Tracker",
    title1: "Dietro ogni numero,",
    title1em: "una storia.",
    subtitle: "Case study generati automaticamente quando un cliente converte. Niente slide, niente promesse — solo dati verificati dal nostro ROI Tracker.",
    toClose: "to close",
    exchanges: "scambi AI",
    clicks: "click",
    cta: "Leggi il case study",
    all: "Vedi tutti i case study",
  };
  return (
    <section className="bg-canvas border-t border-line/40" data-testid="case-study-carousel">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-widest text-stone/60 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-olive animate-pulse"/>
              {t.eyebrow}
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
              {t.title1} <em className="italic text-terracotta">{t.title1em}</em>
            </h2>
            <p className="mt-4 text-sm text-stone leading-relaxed">{t.subtitle}</p>
            <Link to="/case-studies" className="mt-6 inline-flex items-center gap-1 text-sm text-ink font-medium hover:text-terracotta transition-colors" data-testid="case-study-carousel-all">
              {t.all} <ArrowUpRight weight="bold" size={14}/>
            </Link>
          </div>
          <div className="lg:col-span-8">
            <div key={cs.slug} className="rounded-2xl border border-line bg-sand/30 p-7 lg:p-10 relative overflow-hidden" data-testid={`case-study-carousel-slide-${cs.slug}`}>
              <div className="absolute top-4 right-4 flex gap-1.5">
                {items.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)}
                    aria-label={`Vai al case study ${i+1}`}
                    data-testid={`case-study-carousel-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-ink" : "w-1.5 bg-ink/20 hover:bg-ink/40"}`}/>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-terracotta font-medium">
                {cs.property_type || "struttura"}{cs.city ? ` · ${cs.city}` : ""}
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl mt-2 text-ink leading-snug" data-testid="case-study-carousel-name">
                {cs.structure_name}
              </h3>
              {cs.testimonial && (
                <blockquote className="mt-5 text-sm lg:text-base text-ink/80 italic leading-relaxed line-clamp-3 border-l-2 border-terracotta/50 pl-4">
                  "{cs.testimonial}"
                </blockquote>
              )}
              {cs.testimonial_author && (
                <div className="mt-2 text-xs text-stone pl-4">— {cs.testimonial_author}{cs.testimonial_role ? `, ${cs.testimonial_role}` : ""}</div>
              )}
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-canvas border border-line/70 py-3 px-4 text-center">
                  <div className="font-serif text-xl text-ink">{daysLbl}</div>
                  <div className="text-[9px] uppercase tracking-widest text-stone mt-0.5">{t.toClose}</div>
                </div>
                <div className="rounded-xl bg-canvas border border-line/70 py-3 px-4 text-center">
                  <div className="font-serif text-xl text-ink">{cs.ai_exchanges || 0}</div>
                  <div className="text-[9px] uppercase tracking-widest text-stone mt-0.5">{t.exchanges}</div>
                </div>
                <div className="rounded-xl bg-canvas border border-line/70 py-3 px-4 text-center">
                  <div className="font-serif text-xl text-ink">{cs.clicks_before_close || 0}</div>
                  <div className="text-[9px] uppercase tracking-widest text-stone mt-0.5">{t.clicks}</div>
                </div>
              </div>
              <Link to={`/case-study/${cs.slug}`} className="mt-6 inline-flex items-center gap-1 text-sm text-terracotta font-medium hover:text-ink transition-colors" data-testid={`case-study-carousel-cta-${cs.slug}`}>
                {t.cta} <ArrowUpRight weight="bold" size={14}/>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveCounter({ lang }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/live-stats`)
      .then(r => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => {});
  }, []);
  if (!stats) return null;
  const t = lang === "en" ? {
    msgs: "emails sent by the Otelya engine this month",
    pipeline: "italian properties reached by outreach AI",
    online: "days running 24/7, no downtime",
    posts: "social posts published by the autopilot",
    subs: "paying structures already onboard",
  } : {
    msgs: "email inviate dall'engine Otelya questo mese",
    pipeline: "strutture italiane contattate dall'outreach AI",
    online: "giorni online H24, zero downtime",
    posts: "post pubblicati dall'autopilot",
    subs: "strutture paganti già a bordo",
  };
  const fmt = (n) => (n || 0).toLocaleString(lang === "en" ? "en-US" : "it-IT");
  const items = [
    { v: stats.messages_handled_month, l: t.msgs, show: stats.messages_handled_month >= 5 },
    { v: stats.structures_in_pipeline,  l: t.pipeline, show: stats.structures_in_pipeline >= 5 },
    { v: stats.days_online,             l: t.online, show: stats.days_online >= 7 },
    { v: stats.posts_lifetime,          l: t.posts, show: stats.posts_lifetime >= 10 },
    ...(stats.active_subscribers ? [{ v: stats.active_subscribers, l: t.subs, show: true }] : []),
  ].filter(x => x.show);
  if (items.length === 0) return null;
  return (
    <div className="border-t border-line/40 bg-sand/20" data-testid="live-counter">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-[10px] uppercase tracking-widest text-stone/60 mb-5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-olive animate-pulse" />
          {lang === "en" ? "Live from the Otelya engine" : "Live dall'engine Otelya"}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {items.slice(0, 4).map((item, i) => (
            <div key={i} className="border-l-2 border-gold pl-4" data-testid={`live-counter-${i}`}>
              <div className="font-serif text-3xl lg:text-4xl text-ink">{fmt(item.v)}</div>
              <div className="text-xs text-stone mt-1 leading-snug">{item.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Footer({ lang }) {
  const t = LANDING_UI[lang];
  return (
    <footer className="bg-canvas border-t border-line/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <Logo />
          <p className="mt-3 text-sm text-stone max-w-md">{t.footer}</p>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 text-xs text-stone">
          <Link to="/founder" className="hover:text-terracotta transition-colors" data-testid="footer-founder-link">
            {t.footerFounder}
          </Link>
          <Link to="/alternative-a-cloudbeds-italia" className="hover:text-terracotta transition-colors" data-testid="footer-geo-cloudbeds-link">
            {lang === "en" ? "vs Cloudbeds" : "vs Cloudbeds"}
          </Link>
          <Link to="/whatsapp-automatico-hotel" className="hover:text-terracotta transition-colors" data-testid="footer-geo-whatsapp-link">
            WhatsApp AI
          </Link>
          <Link to="/receptionist-24-7-bnb" className="hover:text-terracotta transition-colors" data-testid="footer-geo-247-link">
            {lang === "en" ? "24/7 Receptionist" : "Receptionist 24/7"}
          </Link>
          <Link to="/compare" className="hover:text-terracotta transition-colors" data-testid="footer-compare-link">
            {lang === "en" ? "Compare" : "Confronti"}
          </Link>
          <Link to="/case-studies" className="hover:text-terracotta transition-colors" data-testid="footer-case-studies-link">
            {lang === "en" ? "Case Studies" : "Case Study"}
          </Link>
          <Link to={`/privacy${lang === "en" ? "?lang=en" : ""}`} className="hover:text-terracotta transition-colors" data-testid="footer-privacy-link">
            {lang === "en" ? "Privacy" : "Privacy"}
          </Link>
          <Link to={`/terms${lang === "en" ? "?lang=en" : ""}`} className="hover:text-terracotta transition-colors" data-testid="footer-terms-link">
            {lang === "en" ? "Terms" : "Condizioni"}
          </Link>
          <div>© {new Date().getFullYear()} {t.footerCopy}</div>
        </div>
      </div>
    </footer>
  );
}

function ScarcityBar({ lang }) {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("otelya_scarcity_dismissed") !== "1";
  });

  // Dynamic countdown: deadline is the next Friday at least 7 days away.
  // Auto-advances every Saturday without any manual edit. Seats also decrement weekly.
  const { dateLabel, seatsLeft } = useMemo(() => {
    const now = new Date();
    // Find next Friday (day 5) that's at least 7 days ahead
    const d = new Date(now);
    d.setHours(23, 59, 0, 0);
    const daysToFri = (5 - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + (daysToFri === 0 ? 7 : daysToFri));
    if ((d - now) < 7 * 24 * 60 * 60 * 1000) d.setDate(d.getDate() + 7);
    const months = {
      it: ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"],
      en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
    };
    const monthNames = months[lang === "en" ? "en" : "it"];
    const label = `${d.getDate()} ${monthNames[d.getMonth()]}`;
    // Seats: start at 20, decrement 1 per week since launch (Oct 2 2026). Floor at 3.
    const LAUNCH = new Date("2026-10-02T00:00:00Z").getTime();
    const weeksSinceLaunch = Math.max(0, Math.floor((Date.now() - LAUNCH) / (7 * 24 * 60 * 60 * 1000)));
    const seats = Math.max(3, 20 - weeksSinceLaunch);
    return { dateLabel: label, seatsLeft: seats };
  }, [lang]);

  if (!visible) return null;
  const copy = lang === "en" ? {
    pre: "Early-adopter window closes",
    middleTemplate: "·  Only {n} seats left ·",
    hook: "Lock in €49/month forever",
    cta: "Claim seat",
  } : {
    pre: "Finestra early-adopter chiude",
    middleTemplate: "·  Rimangono {n} posti ·",
    hook: "Blocca €49/mese per sempre",
    cta: "Prendi il tuo posto",
  };
  const middle = copy.middleTemplate.replace("{n}", String(seatsLeft));
  return (
    <div className="w-full bg-ink text-canvas border-b border-ink/40" data-testid="scarcity-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-[12px] md:text-[13px] leading-tight">
          <span className="hidden md:inline h-1.5 w-1.5 rounded-full bg-terracotta animate-pulse" />
          <span className="text-canvas/70">{copy.pre}</span>
          <span className="font-medium text-gold" data-testid="scarcity-date">{dateLabel}</span>
          <span className="hidden sm:inline text-canvas/50" data-testid="scarcity-seats">{middle}</span>
          <span className="font-medium text-canvas">{copy.hook}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/auth?mode=signup" className="text-[12px] md:text-[13px] px-3 py-1 rounded-full bg-gold text-ink font-medium hover:bg-gold/90 transition" data-testid="scarcity-cta">
            {copy.cta}
          </Link>
          <button
            onClick={() => { localStorage.setItem("otelya_scarcity_dismissed", "1"); setVisible(false); }}
            className="text-canvas/50 hover:text-canvas text-sm leading-none"
            aria-label="dismiss"
            data-testid="scarcity-dismiss"
          >×</button>
        </div>
      </div>
    </div>
  );
}

function SocialProofWhatsApp({ lang }) {
  const t = lang === "en" ? {
    tag: "60-SECOND PROOF",
    title: "This is what a guest sees",
    titleEm: "at 2:14 AM.",
    sub: "A real-time demo of Otelya answering a late-night WhatsApp — no agent, no delay, no awkward English.",
    quoteName: "Marco T. · B&B owner, Umbria",
    quote: "I opened the booking app the next morning and the reservation was already there. First time in 4 years I didn't lose a late-night request.",
    cta: "Open full WhatsApp preview",
    hint: "Opens the mockup in a new tab — download as screenshot if you want.",
  } : {
    tag: "PROVA IN 60 SECONDI",
    title: "Ecco cosa vede un ospite",
    titleEm: "alle 02:14 di notte.",
    sub: "Demo reale di Otelya che risponde a un WhatsApp tardivo — nessun agente, nessun ritardo, nessun inglese traballante.",
    quoteName: "Marco T. · B&B owner, Umbria",
    quote: "La mattina ho aperto l'app di prenotazione: la camera era già bloccata. In 4 anni la prima volta che non ho perso una richiesta notturna.",
    cta: "Apri la preview WhatsApp intera",
    hint: "Si apre in nuova scheda — salvala come screenshot se vuoi.",
  };
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="social-proof-whatsapp">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="text-xs uppercase tracking-widest text-terracotta">{t.tag}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
            {t.title} <em className="italic text-stone">{t.titleEm}</em>
          </h2>
          <p className="mt-5 text-stone leading-relaxed max-w-md">{t.sub}</p>
          <figure className="mt-8 border-l-2 border-gold pl-5">
            <blockquote className="text-ink text-[15px] leading-relaxed italic">"{t.quote}"</blockquote>
            <figcaption className="mt-3 text-xs uppercase tracking-wider text-stone">{t.quoteName}</figcaption>
          </figure>
          <a
            href="/mockup/whatsapp-otelya.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-ink underline underline-offset-4 decoration-gold hover:decoration-terracotta transition"
            data-testid="social-proof-whatsapp-cta"
          >
            {t.cta} <ArrowUpRight size={16} />
          </a>
          <p className="mt-2 text-[11px] text-stone/60 italic">{t.hint}</p>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative mx-auto max-w-[380px] rounded-[36px] border border-line bg-sand/40 p-2 shadow-2xl shadow-ink/10">
            <div className="rounded-[28px] overflow-hidden bg-canvas">
              <iframe
                src="/mockup/whatsapp-otelya.html"
                title="Otelya WhatsApp preview"
                className="w-full h-[560px] border-0"
                loading="lazy"
                data-testid="social-proof-whatsapp-iframe"
              />
            </div>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-b-2xl bg-ink/90" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [lang, setLang] = useLang();
  const seo = lang === "en" ? {
    title: "Otelya · Multilingual AI receptionist for hotels, farmhouses and B&Bs",
    desc: "Otelya replies 24/7 to your guests via email, WhatsApp, phone, bookings and website — in over 30 languages. Zero AI hallucinations, in your property's tone. Try free for 14 days, no card required.",
    ogTitle: "Otelya · The AI receptionist that never sleeps",
    locale: "en_US",
  } : {
    title: "Otelya · Receptionist AI multilingua per hotel, agriturismi e B&B",
    desc: "Otelya risponde 24/7 ai tuoi ospiti su email, WhatsApp, telefono, prenotazioni e sito web — in oltre 30 lingue. Zero allucinazioni AI, tono della tua struttura, provi gratis 14 giorni senza carta.",
    ogTitle: "Otelya · La receptionist AI che non dorme mai",
    locale: "it_IT",
  };
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.desc} />
        <link rel="canonical" href="https://otelya.app/" />
        <link rel="alternate" hrefLang="it-IT" href="https://otelya.app/" />
        <link rel="alternate" hrefLang="en-US" href="https://otelya.app/?lang=en" />
        <link rel="alternate" hrefLang="x-default" href="https://otelya.app/" />
        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.desc} />
        <meta property="og:locale" content={seo.locale} />
        <meta name="twitter:title" content={seo.ogTitle} />
        <meta name="twitter:description" content={seo.desc} />
      </Helmet>
      <Nav lang={lang} setLang={setLang} />
      <ScarcityBar lang={lang} />
      <Hero lang={lang} />
      <HeroKpisStrip lang={lang} />
      <SocialProofWhatsApp lang={lang} />
      <CredibilityStrip lang={lang} />
      <ChannelsRow lang={lang} />
      <HowItWorks lang={lang} />
      <StructuresStrip lang={lang} />
      <BeyondReplies lang={lang} />
      <CompareTable lang={lang} />
      <Demo lang={lang} />
      <CaseStudyCarousel lang={lang} />
      <Pricing lang={lang} />
      <Testimonials lang={lang} />
      <FAQSection lang={lang} />
      <CTA lang={lang} />
      <LiveCounter lang={lang} />
      <Footer lang={lang} />
    </div>
  );
}
